import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CONTAINER_ID = "__live_chat_embed_container__";
const STATE_KEY = "__lc_state__";
const HIDE_STYLE_ID = "__lc_hide_off_home__";

// CSS that hides the 3rd-party widget on every non-home route.
// Activated by adding the `data-lc-hide="1"` attribute to <html>.
const HIDE_CSS = `
  html[data-lc-hide="1"] #${CONTAINER_ID},
  html[data-lc-hide="1"] #chat-widget-container,
  html[data-lc-hide="1"] iframe#chat-widget,
  html[data-lc-hide="1"] iframe[title*="chat" i][src*="tawk"],
  html[data-lc-hide="1"] #crisp-chatbox,
  html[data-lc-hide="1"] iframe#tidio-chat-iframe,
  html[data-lc-hide="1"] jdiv,
  html[data-lc-hide="1"] .intercom-lightweight-app,
  html[data-lc-hide="1"] .intercom-launcher-frame,
  html[data-lc-hide="1"] .intercom-app,
  html[data-lc-hide="1"] iframe[title*="Messaging" i],
  html[data-lc-hide="1"] iframe[title*="Web Widget" i] {
    display: none !important;
    visibility: hidden !important;
    pointer-events: none !important;
  }
`;

/**
 * Loads & injects 3rd-party live chat embed code from /api/public/live-chat.
 * - Injects only once on mount (when on home page).
 * - On every route change, toggles <html data-lc-hide> so the widget is
 *   shown ONLY on the home page (`/`) and hidden everywhere else.
 */
export default function LiveChatLoader() {
  const mounted = useRef(false);
  const location = useLocation();

  // Toggle visibility on every route change — show only on `/`.
  useEffect(() => {
    if (typeof document === "undefined") return;
    // Ensure hide-CSS is in the document head
    if (!document.getElementById(HIDE_STYLE_ID)) {
      const s = document.createElement("style");
      s.id = HIDE_STYLE_ID;
      s.textContent = HIDE_CSS;
      document.head.appendChild(s);
    }
    const isHome = location.pathname === "/" || location.pathname === "";
    if (isHome) {
      document.documentElement.removeAttribute("data-lc-hide");
    } else {
      document.documentElement.setAttribute("data-lc-hide", "1");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;

    let cancelled = false;

    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/live-chat`);
        if (cancelled) return;
        if (!data?.enabled || !data?.embed_code) return;

        // Avoid double-injection across SPA navigations / hot-reload
        if (window[STATE_KEY]?.injected) return;

        const container = document.createElement("div");
        container.id = CONTAINER_ID;
        container.style.position = "fixed";
        container.style.left = "0";
        container.style.top = "0";
        container.style.width = "0";
        container.style.height = "0";
        container.style.pointerEvents = "none";
        container.style.zIndex = "0";
        container.innerHTML = data.embed_code;
        document.body.appendChild(container);

        // <script> tags inserted via innerHTML do NOT execute — re-create them.
        const scripts = container.querySelectorAll("script");
        scripts.forEach((oldScript) => {
          const s = document.createElement("script");
          for (const { name, value } of Array.from(oldScript.attributes)) {
            try { s.setAttribute(name, value); } catch (_) { /* skip invalid attr */ }
          }
          if (oldScript.src) {
            // External script — re-create with src so the browser fetches & runs it.
            s.async = oldScript.async || true;
          } else {
            s.text = oldScript.textContent || "";
          }
          oldScript.parentNode.replaceChild(s, oldScript);
        });

        // ---------------------------------------------------------------
        // Position override — every 3rd-party widget (LiveChat.com / Tawk.to /
        // Crisp / Tidio / JivoChat / Intercom / Zendesk) injects its own
        // floating bubble at its own default position. Force them all into
        // the EXACT same spot as our in-house Max_Ai bubble, so the site
        // never shows the bubble at a different place when the admin
        // switches providers.
        //
        // Coordinates here match `FloatingSupport` in `LeftSidebar.jsx`:
        //   bottom: 5rem (lg: 4rem)  ·  right: 0.75rem (sm: 1rem)
        // ---------------------------------------------------------------
        if (!document.getElementById("__lc_position_override__")) {
          const style = document.createElement("style");
          style.id = "__lc_position_override__";
          style.textContent = `
            /* LiveChat.com */
            #chat-widget-container,
            iframe#chat-widget,
            /* Tawk.to */
            iframe[title*="chat" i][src*="tawk"],
            /* Crisp */
            #crisp-chatbox > div,
            /* Tidio */
            iframe#tidio-chat-iframe,
            /* JivoChat */
            jdiv,
            /* Intercom */
            .intercom-lightweight-app,
            .intercom-launcher-frame,
            /* Zendesk */
            iframe[title*="Messaging" i],
            iframe[title*="Web Widget" i] {
              position: fixed !important;
              right: 0.75rem !important;
              bottom: 5rem !important;
              left: auto !important;
              top: auto !important;
              z-index: 40 !important;
            }
            @media (min-width: 640px) {
              #chat-widget-container,
              iframe#chat-widget,
              iframe[title*="chat" i][src*="tawk"],
              #crisp-chatbox > div,
              iframe#tidio-chat-iframe,
              jdiv,
              .intercom-lightweight-app,
              .intercom-launcher-frame,
              iframe[title*="Messaging" i],
              iframe[title*="Web Widget" i] {
                right: 1rem !important;
              }
            }
            @media (min-width: 1024px) {
              #chat-widget-container,
              iframe#chat-widget,
              iframe[title*="chat" i][src*="tawk"],
              #crisp-chatbox > div,
              iframe#tidio-chat-iframe,
              jdiv,
              .intercom-lightweight-app,
              .intercom-launcher-frame,
              iframe[title*="Messaging" i],
              iframe[title*="Web Widget" i] {
                bottom: 4rem !important;
              }
            }
          `;
          document.head.appendChild(style);
        }

        window[STATE_KEY] = { injected: true };
      } catch (e) {
        // Silent failure — live chat is non-critical UI.
        // eslint-disable-next-line no-console
        console.warn("[live-chat] load failed:", e?.message || e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}
